/**
 * Standalone Payment Link API Service
 * Client for standalone payment link endpoints
 */

const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3001';
const DEFAULT_TIMEOUT = 30000;

function createTimeoutController(timeout) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
}

async function handleResponse(response) {
  const data = await response.json();

  if (!response.ok) {
    const errorMessage = data.error || data.errorMessage || 'Error en el servidor';
    const error = new Error(errorMessage);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

// ─── Admin Endpoints ────────────────────────────────────────────

/**
 * Generate a new standalone payment link
 */
export async function generateLink(data, authToken, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/admin/standalone-links`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(data),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }
    throw error;
  }
}

/**
 * List standalone payment links
 */
export async function getLinks(filters, authToken, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const params = new URLSearchParams();
    if (filters.paymentStatus) params.set('paymentStatus', filters.paymentStatus);
    if (filters.courseId) params.set('courseId', filters.courseId);
    if (filters.limit) params.set('limit', filters.limit);
    if (filters.offset) params.set('offset', filters.offset);

    const response = await fetch(`${PAYMENT_API_URL}/api/admin/standalone-links?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }
    throw error;
  }
}

/**
 * Cancel a standalone payment link
 */
export async function cancelLink(id, authToken, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/admin/standalone-links/${id}/cancel`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }
    throw error;
  }
}

// ─── Public Endpoints ───────────────────────────────────────────

/**
 * Get standalone link data
 */
export async function getLinkData(token, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/standalone-payment/${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }
    if (error.status === 410) {
      const err = new Error(error.message || 'Este enlace ha expirado.');
      err.status = 410;
      err.expired = error.data?.expired;
      err.cancelled = error.data?.cancelled;
      throw err;
    }
    if (error.status === 400 && error.data?.alreadyPaid) {
      const err = new Error('Este enlace ya fue utilizado.');
      err.status = 400;
      err.alreadyPaid = true;
      throw err;
    }
    if (error.status) throw error;
    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Register student data on a standalone link
 */
export async function registerStudent(token, studentData, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/standalone-payment/${encodeURIComponent(token)}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(studentData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }
    if (error.status) throw error;
    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Create WebPay transaction for standalone link
 */
export async function createStandalonePayment(token, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/standalone-payment/${encodeURIComponent(token)}/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }
    if (error.status === 410) {
      const err = new Error('Este enlace ha expirado.');
      err.expired = true;
      throw err;
    }
    if (error.status === 400 && error.data?.alreadyPaid) {
      const err = new Error('Este enlace ya fue utilizado.');
      err.alreadyPaid = true;
      throw err;
    }
    if (error.status) throw error;
    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Confirm standalone payment after WebPay redirect
 */
export async function confirmStandalonePayment(confirmData, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/standalone-payment/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(confirmData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('La confirmación ha excedido el tiempo de espera.');
    }
    if (error.status) throw error;
    throw new Error('Error de conexión al confirmar el pago.');
  }
}

/**
 * Redirect to WebPay payment form
 */
export function redirectToWebPay(webpayUrl, webpayToken) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = webpayUrl;
  form.style.display = 'none';

  const tokenInput = document.createElement('input');
  tokenInput.type = 'hidden';
  tokenInput.name = 'token_ws';
  tokenInput.value = webpayToken;
  form.appendChild(tokenInput);

  document.body.appendChild(form);
  form.submit();
}

/**
 * Parse WebPay redirect parameters
 */
export function parseWebPayParams(search = window.location.search) {
  const params = new URLSearchParams(search);
  return {
    token_ws: params.get('token_ws') || undefined,
    TBK_TOKEN: params.get('TBK_TOKEN') || undefined,
    TBK_ORDEN_COMPRA: params.get('TBK_ORDEN_COMPRA') || undefined,
    TBK_ID_SESION: params.get('TBK_ID_SESION') || undefined,
  };
}

export function getPaymentResultType(params) {
  if (params.token_ws) return 'success';
  if (params.TBK_TOKEN) return 'cancelled';
  if (params.TBK_ORDEN_COMPRA && params.TBK_ID_SESION) return 'timeout';
  return 'unknown';
}
